import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import WatchDetail from "@/pages/WatchDetail";
import Movements from "@/pages/Movements";
import MovementDetail from "@/pages/MovementDetail";
import Workshop from "@/pages/Workshop";
import PartsShoppingList from "@/pages/PartsShoppingList";
import Suppliers from "@/pages/Suppliers";
import Data from "@/pages/Data";
import ValidationManager from "@/pages/ValidationManager";
import StockParts from "@/pages/StockParts";
import Sales from "@/pages/Sales";
import Sourcing from "@/pages/Sourcing";
import InspectionSetup from "@/pages/InspectionSetup";
import Library from "@/pages/Library";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PublicPortfolio from "@/pages/PublicPortfolio";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { LayoutDashboard, ListOrdered, Cog, Wrench, Package, Store, LogOut, FileSpreadsheet, Menu, Library as LibraryIcon, Receipt, Target } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/authContext";
import { TickdocLogo } from "@/components/TickdocLogo";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/inventory", label: "Inventory", icon: ListOrdered, testid: "nav-inventory" },
  { to: "/movements", label: "Movements", icon: Cog, testid: "nav-movements" },
  { to: "/workshop", label: "Workshop", icon: Wrench, testid: "nav-workshop" },
  { to: "/parts", label: "Parts", icon: Package, testid: "nav-parts" },
  { to: "/sales", label: "Sales", icon: Receipt, testid: "nav-sales" },
  { to: "/sourcing", label: "Sourcing", icon: Target, testid: "nav-sourcing" },
  { to: "/suppliers", label: "Suppliers", icon: Store, testid: "nav-suppliers" },
  { to: "/library", label: "Library", icon: LibraryIcon, testid: "nav-library" },
  { to: "/data", label: "Data", icon: FileSpreadsheet, testid: "nav-data" },
];

function Sidebar() {
  const linkBase =
    "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-200 border-l-2 border-transparent";
  const active = "text-[#3A2A10] border-l-[#D4AF37] bg-[#2B2214]/[0.14]";
  const idle = "text-[#3A2A10] hover:text-[#0A0604] hover:bg-[#2B2214]/[0.06]";
  const { user, logout } = useAuth();

  return (
    <aside
      className="hidden md:flex w-60 shrink-0 flex-col border-r border-[#8B6A1F]/35 bg-[#FBF3D9] sticky top-0 h-screen"
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-[#8B6A1F]/35 flex items-center justify-center">
        <TickdocLogo
          variant="full"
          className="w-full max-w-[190px] text-[#0A0604]"
          title="tickdoc — Buy · Sell · Restore"
        />
      </div>
      <nav className="flex flex-col py-4">
        {NAV_ITEMS.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}
            data-testid={testid}
          >
            <Icon size={16} /> {label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto border-t border-[#8B6A1F]/35 p-5 space-y-2">
        {user && (
          <div className="flex items-center gap-3" data-testid="sidebar-user">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-[#8B6A1F]/45" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#8B6A1F]/30 flex items-center justify-center text-[#0A0604] text-xs font-medium">
                {user.name?.[0] || user.email?.[0] || "?"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#0A0604] truncate" title={user.name || user.email}>
                {user.name || "Owner"}
              </div>
              <div className="text-[10px] text-[#3A2A10] truncate" title={user.email}>{user.email}</div>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full inline-flex items-center justify-center gap-2 h-8 text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] hover:text-[#0A0604] border border-[#8B6A1F]/35 hover:border-[#8B6A1F]/60 rounded-sm transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </aside>
  );
}

/** Mobile (< md): slim top bar with hamburger that opens a slide-in nav drawer. */
function MobileTopBar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  // Close the drawer whenever the route changes
  const linkBase =
    "flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 border-transparent";
  const active = "text-[#3A2A10] border-l-[#D4AF37] bg-[#2B2214]/[0.14]";
  const idle = "text-[#3A2A10]";

  return (
    <>
      <div
        className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 bg-[#FBF3D9] border-b border-[#8B6A1F]/35"
        data-testid="mobile-topbar"
      >
        <button
          onClick={() => setOpen(true)}
          aria-label="Open navigation menu"
          className="h-10 w-10 inline-flex items-center justify-center text-[#3A2A10] rounded-sm"
          data-testid="mobile-menu-button"
        >
          <Menu size={22} />
        </button>
        <TickdocLogo
          variant="full"
          className="h-10 w-auto text-[#0A0604]"
          title="tickdoc"
        />
        <div className="w-10" aria-hidden />
      </div>

      {/* Drawer */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-50"
          data-testid="mobile-nav-drawer"
        >
          <div
            className="absolute inset-0 bg-[#0A0604]/45"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 h-full w-[82%] max-w-[320px] bg-[#FBF3D9] border-r border-[#8B6A1F]/35 flex flex-col shadow-xl">
            <div className="px-5 py-4 border-b border-[#8B6A1F]/35 flex items-center justify-center">
              <TickdocLogo
                variant="full"
                className="w-full max-w-[180px] text-[#0A0604]"
                title="tickdoc"
              />
            </div>
            <nav className="flex flex-col py-2" aria-label="Main navigation">
              {NAV_ITEMS.map(({ to, label, icon: Icon, testid, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `${linkBase} ${isActive ? active : idle}`}
                  data-testid={`mobile-${testid}`}
                >
                  <Icon size={18} /> {label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-auto border-t border-[#8B6A1F]/35 p-5 space-y-3">
              {user && (
                <div className="flex items-center gap-3" data-testid="mobile-sidebar-user">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-[#8B6A1F]/45" />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-[#8B6A1F]/30 flex items-center justify-center text-[#0A0604] text-sm font-medium">
                      {user.name?.[0] || user.email?.[0] || "?"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0A0604] truncate">{user.name || "Owner"}</div>
                    <div className="text-[11px] text-[#3A2A10] truncate">{user.email}</div>
                  </div>
                </div>
              )}
              <button
                onClick={() => { setOpen(false); logout(); }}
                className="w-full inline-flex items-center justify-center gap-2 h-10 text-[11px] uppercase tracking-[0.25em] text-[#3A2A10] border border-[#8B6A1F]/45 rounded-sm"
                data-testid="mobile-sidebar-logout"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
      {/* Render location as hidden dependency so the hook closure re-evaluates */}
      <span className="hidden" aria-hidden>{location.pathname}</span>
    </>
  );
}

/** Everything inside the shell (sidebar + routed pages). Protected. */
function AuthedShell() {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <MobileTopBar />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/inventory/:id" element={<WatchDetail />} />
          <Route path="/movements" element={<Movements />} />
          <Route path="/movements/:id" element={<MovementDetail />} />
          <Route path="/workshop" element={<Workshop />} />
          <Route path="/parts" element={<PartsShoppingList />} />
          <Route path="/sales" element={<Sales />} />
          <Route path="/sourcing" element={<Sourcing />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/library" element={<Library />} />
          <Route path="/data" element={<Data />} />
          <Route path="/data/validation" element={<ValidationManager />} />
          <Route path="/data/inspection" element={<InspectionSetup />} />
          <Route path="/data/stock-parts" element={<StockParts />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent auth fragment synchronously BEFORE any other route runs,
  // so the session_id exchange completes before ProtectedRoute fires off a /me.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public, no-auth restoration showcase. Lives outside ProtectedRoute
          so anonymous visitors (Reddit / Instagram / eBay buyers) can view. */}
      <Route path="/portfolio/:id" element={<PublicPortfolio />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AuthedShell />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain-overlay">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
        <Toaster theme="light" position="top-right" />
      </BrowserRouter>
    </div>
  );
}
